/**
 * Hides the Candy Rack mattress upsell ("Har du husket madrass?") when the customer added a
 * bundle that already contains the mattress. Customers who pick "Kun seng" still get the offer.
 *
 * WHY THIS IS DONE IN THE THEME
 * Candy Rack is a third-party app (candyrack.ds-cdn.com). Its offer is attached to the bed
 * product, and it has no knowledge of this theme's bundle selector, so it cannot tell
 * "bed only" from "bed + madrass" on its own.
 *
 * HOW THE POPUP IS FOUND
 * Candy Rack's markup is a hashed build with no stable id or class on the modal itself — the one
 * durable, self-declared hook is the `candyrack-body-open` class it sets on <body> while the popup
 * is open (seen in candyrack-popup-app). So instead of guessing a selector we:
 *   1. snapshot the direct children of <body> at page load, before any popup can exist;
 *   2. treat anything added to <body> afterwards as a candidate;
 *   3. when the popup is open, hide the candidate that looks like a modal (fixed position,
 *      stacked above the page, visible).
 * The theme's own drawers are server-rendered and therefore in the load-time snapshot, so they
 * are never candidates.
 *
 * WHY IT CAN NEVER BLOCK AN ADD-TO-CART
 * Suppression is armed only by the theme's own `cart:update` event, dispatched by
 * product-form-component AFTER the items were added successfully. So:
 *   - normal case: items are in the cart, the popup is pure noise -> hidden.
 *   - if Candy Rack ever swallows the submit so our add never runs, no event fires, nothing is
 *     hidden, and the customer keeps the popup's own "FORTSETT" button to complete the purchase.
 *
 * Verify with `window.__bundleUpsellDebug()` in the console — see the bottom of this file.
 */

const BODY_OPEN_CLASS = 'candyrack-body-open';

// How long after a confirmed add we keep watching. Covers apps that open the popup after the
// cart request rather than on click, without muting a later, unrelated add-to-cart.
const SUPPRESS_WINDOW_MS = 8000;

// Elements at or above this z-index count as "an overlay on top of the page".
const OVERLAY_MIN_Z_INDEX = 100;

/** Direct children of <body> that existed before any popup could have been created. */
const baseline = new WeakSet();
/** Nodes added to <body> after load, in insertion order. */
let candidates = [];

for (const child of Array.from(document.body.children)) baseline.add(child);

const bodyChildObserver = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node instanceof HTMLElement && !baseline.has(node)) candidates.push(node);
    }
  }
  // Keep the list from growing without bound on long sessions.
  if (candidates.length > 40) candidates = candidates.slice(-40);
});
bodyChildObserver.observe(document.body, { childList: true });

/**
 * True when the currently selected package adds mattresses on top of the bed. The base
 * ("Kun seng") option carries an empty `data-bundle-variant-ids`, so it reads as false and the
 * upsell is left alone — which is exactly the case the offer is meant for.
 * @returns {boolean}
 */
function selectedPackageIncludesAddons() {
  const selected = /** @type {HTMLInputElement | null} */ (
    document.querySelector('[data-bundle-package]:checked')
  );
  if (!selected) return false;
  return (selected.dataset.bundleVariantIds || '').trim() !== '';
}

/**
 * Does this element look like a modal sitting on top of the page?
 * @param {HTMLElement} el
 * @returns {boolean}
 */
function looksLikeOverlay(el) {
  if (!el.isConnected) return false;
  if (['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE', 'NOSCRIPT'].includes(el.tagName)) return false;

  const style = getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden') return false;
  if (style.position !== 'fixed' && style.position !== 'absolute') return false;

  const zIndex = parseInt(style.zIndex, 10);
  if (Number.isNaN(zIndex) || zIndex < OVERLAY_MIN_Z_INDEX) return false;

  // Must actually cover something — guards against invisible 0x0 helper nodes.
  const rect = el.getBoundingClientRect();
  return rect.width > 50 && rect.height > 50;
}

/**
 * @param {HTMLElement} el
 */
function hideElement(el) {
  if (el.dataset.bundleUpsellHidden === 'true') return;
  el.dataset.bundleUpsellHidden = 'true';
  el.style.setProperty('display', 'none', 'important');
}

/** Hides the open popup and releases the scroll lock it applied. @returns {number} hidden count */
function suppressNow() {
  if (!document.body.classList.contains(BODY_OPEN_CLASS)) return 0;

  let hidden = 0;
  for (const el of candidates) {
    if (looksLikeOverlay(el)) {
      hideElement(el);
      hidden += 1;
    }
  }

  if (hidden > 0) {
    document.body.classList.remove(BODY_OPEN_CLASS);
    document.body.style.removeProperty('overflow');
    document.documentElement.style.removeProperty('overflow');
  }
  return hidden;
}

/** @type {MutationObserver | null} */
let armedObserver = null;
/** @type {number | undefined} */
let disarmTimer;

function disarm() {
  armedObserver?.disconnect();
  armedObserver = null;
  clearTimeout(disarmTimer);
  disarmTimer = undefined;
}

/**
 * Watches for the popup opening for a short window. The popup may already be open by the time we
 * arm (Candy Rack opens on click, our add resolves a moment later), so we also sweep immediately.
 */
function arm() {
  suppressNow();

  if (!armedObserver) {
    armedObserver = new MutationObserver(() => suppressNow());
    // `class` on <body> tells us the popup opened; childList catches it mounting afterwards.
    armedObserver.observe(document.body, { attributes: true, attributeFilter: ['class'], childList: true });
  }

  clearTimeout(disarmTimer);
  disarmTimer = setTimeout(disarm, SUPPRESS_WINDOW_MS);
}

document.addEventListener('cart:update', (event) => {
  const data = /** @type {CustomEvent} */ (event).detail?.data;

  // Only our own product form, and only a successful add.
  if (data?.source !== 'product-form-component' || data?.didError) return;
  if (!selectedPackageIncludesAddons()) return;

  arm();
});

/**
 * Console helper for verifying on a real page:
 *   1. open a bed product page, choose "seng + madrass", add to cart
 *   2. run `__bundleUpsellDebug()`
 * `armed` should be true, `popupOpen` false and `hidden` >= 1 once the popup was caught.
 */
window.__bundleUpsellDebug = () => ({
  bundleSelectedWithAddons: selectedPackageIncludesAddons(),
  armed: armedObserver !== null,
  popupOpen: document.body.classList.contains(BODY_OPEN_CLASS),
  candidates: candidates.length,
  overlayCandidates: candidates.filter(looksLikeOverlay).length,
  hidden: document.querySelectorAll('[data-bundle-upsell-hidden="true"]').length,
});
